<ng-container *ngIf="loading">
  <div class="loading-bar">
    <tm1-progress-ring value="" min="0" max="1" size="regular"></tm1-progress-ring>
  </div>
</ng-container>

<div class="detail-page" *ngIf="!loading">
  <button type="button" class="back-button" (click)="goBack()" aria-label="Back to dashboard">
    <svg class="back-button-icon" viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 4l-6 6 6 6"/>
    </svg>
    <span>Dashboard</span>
  </button>

  <h1 class="title">{{ performanceTitle }}</h1>
  <div class="subtitle">{{ performanceSubtitle }}</div>

  <div *ngIf="loadError" class="performance-error">
    <tm1-banner appearance="error">{{ loadError }}</tm1-banner>
  </div>

  <div class="status-banner"
       [class.status-banner--el]="currentStage.code === 'EL'"
       [class.status-banner--tc]="currentStage.code === 'TC'"
       [class.status-banner--tm1]="currentStage.code === 'TM1'"
       [class.status-banner--tst]="currentStage.code === 'TST' && !isEventDayReady"
       [class.status-banner--ready]="isEventDayReady">
    <div class="status-meta-col">
      <div class="status-label">
        <span>CURRENT STATUS</span>
        <span class="status-lock"
              *ngIf="currentStage.code === 'TST' && tm1Configuration"
              [appTooltip]="'TM1 configured ' + formatAuditTime(tm1Configuration.time) + ' by ' + tm1Configuration.user"
              aria-label="Locked">
          <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="7" width="10" height="7" rx="1.5"/>
            <path d="M5 7V5a3 3 0 0 1 6 0v2"/>
          </svg>
        </span>
        <span class="status-lock"
              *ngIf="currentStage.code === 'TM1' && clientConfirmation && !tm1EditUnlocked"
              [appTooltip]="'Locked — confirmed by ' + clientConfirmation.user + ' on ' + formatAuditTime(clientConfirmation.time)"
              aria-label="Locked">
          <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="7" width="10" height="7" rx="1.5"/>
            <path d="M5 7V5a3 3 0 0 1 6 0v2"/>
          </svg>
        </span>
        <span class="status-lock status-lock--unlocked"
              *ngIf="currentStage.code === 'TM1' && tm1EditUnlocked"
              appTooltip="Editing unlocked — changes save immediately"
              aria-label="Unlocked">
          <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="7" width="10" height="7" rx="1.5"/>
            <path d="M5 7V5a3 3 0 0 1 6-1.5"/>
          </svg>
        </span>
      </div>
      <div class="status-row">
        <span class="status-dot"></span>
        <span class="status-title" *ngIf="!isEventDayReady">{{ currentStage.activeLabel || (currentStage.label + ' ' + currentStage.sub) }}</span>
        <span class="status-title" *ngIf="isEventDayReady">Event day ready</span>
      </div>
      <div class="status-meta">{{ currentStageStatusMeta }}</div>
      <button type="button" class="audit-trigger" (click)="openAuditModal()">
        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="8" cy="8" r="6"/>
          <path d="M8 4.5V8l2 1.5"/>
        </svg>
        <span>Audit trail</span>
        <span class="audit-trigger-count" *ngIf="audit.length">{{ audit.length }}</span>
      </button>
    </div>

    <div class="status-actions-row">
      <div class="status-action status-action--back" *ngIf="canBacktrack">
        <button type="button"
                class="btn-stage btn-stage--back"
                [disabled]="hasOpenDraft() || saving || backSaving"
                (click)="openBackModal()">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 4l-6 6 6 6"/>
          </svg>
          <span>Roll back to {{ previousStage!.label }} {{ previousStage!.sub }}</span>
        </button>
      </div>

      <div class="status-action" *ngIf="nextStage">
        <button type="button"
                class="btn-stage btn-stage--advance"
                [disabled]="hasOpenDraft() || saving || advanceSaving"
                (click)="openAdvanceModal()">
          <span>Proceed to {{ nextStage.label }} {{ nextStage.sub }}</span>
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M8 4l6 6-6 6"/>
          </svg>
        </button>
        <div class="tiny" *ngIf="hasOpenDraft()">Save or cancel the in-progress row first.</div>
      </div>
      <div class="status-action" *ngIf="!nextStage">
        <button type="button"
                class="btn-stage btn-stage--ready"
                *ngIf="!isEventDayReady"
                [disabled]="!canMarkReady"
                [appTooltip]="markReadyTooltip"
                (click)="markReady()">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 10l4 4 8-8"/>
          </svg>
          <span>{{ markReadySaving ? 'Marking ready…' : 'Mark performance ready' }}</span>
        </button>
        <div class="status-ready-pill" *ngIf="isEventDayReady">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 10l4 4 8-8"/>
          </svg>
          <span>Event day ready</span>
        </div>
        <div class="tiny" *ngIf="!isEventDayReady && !allTestsPassed">Closes out when all tests pass.</div>
        <div class="tiny" *ngIf="markReadyError" style="color:#b91c1c">{{ markReadyError }}</div>
      </div>
    </div>
  </div>

  <div class="stage-tracker">
    <div class="stage-bands">
      <div class="stage-band stage-band--client">Client</div>
      <div class="stage-band stage-band--tm">Ticketmaster</div>
    </div>
    <div class="stage-line stage-line--client"></div>
    <div class="stage-line stage-line--tm"></div>
    <div class="stage"
         *ngFor="let s of stages; let i = index"
         [class.stage--client]="i < 2"
         [class.stage--tm]="i >= 2">
      <span class="stage-circle"
            [class.stage-circle--active]="i === activeStageIndex"
            [class.stage-circle--done]="i < activeStageIndex"></span>
      <div class="stage-label">{{ s.label }}</div>
      <div class="stage-sub">{{ s.sub }}</div>
    </div>
  </div>

  <div class="detail-grid">
    <div class="surface-card events-card events-card--el events-card--full" *ngIf="currentStage.code === 'EL'">
      <div class="card-pad">
        <div class="section-head">
          <div class="section-head-titleblock">
            <div class="section-eyebrow section-eyebrow--el">EVENT LISTING</div>
            <h2 class="section-title">Review and confirm</h2>
          </div>
          <div class="section-head-actions">
            <button type="button"
                    class="btn-download"
                    [disabled]="!visibleEventsForListing().length"
                    appTooltip="Download the visible event listing as an Excel file"
                    aria-label="Download event listing"
                    (click)="downloadEventListing()">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 3v10"/>
                <path d="M5 10l5 5 5-5"/>
                <path d="M3 17h14"/>
              </svg>
            </button>
            <input #importInput
                   type="file"
                   class="visually-hidden"
                   accept=".xlsx,.xls"
                   (change)="onImportFileSelected($event)" />
            <button type="button"
                    class="btn-download"
                    [disabled]="importParsing"
                    appTooltip="Import an edited event listing to update KEEP / REMOVE"
                    aria-label="Import event listing"
                    (click)="importInput.click()">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 17V7"/>
                <path d="M5 12l5-5 5 5"/>
                <path d="M3 3h14"/>
              </svg>
            </button>
            <button type="button"
                    class="btn-add-event"
                    [disabled]="hasOpenDraft() || saving"
                    (click)="startEventDraft()">
              + Add event
            </button>
          </div>
        </div>
        <p class="section-sub">
          Review the events Archtics returned and decide which to KEEP or REMOVE. Expand an event to inspect its barcodes.
          Adding extended tests and editing barcodes happens in the next stage.
        </p>

        <div class="import-error" *ngIf="importError && !importPreview">{{ importError }}</div>

        <div class="event-draft" *ngIf="eventDraft">
          <div class="draft-tag">NEW · add a new event</div>

          <div class="event-type-toggle" role="tablist" aria-label="Event type">
            <button type="button"
                    role="tab"
                    class="event-type-btn"
                    [class.event-type-btn--active]="eventDraft.eventType === 'archtics'"
                    [attr.aria-selected]="eventDraft.eventType === 'archtics'"
                    (click)="setEventDraftType('archtics')">Archtics</button>
            <button type="button"
                    role="tab"
                    class="event-type-btn"
                    [class.event-type-btn--active]="eventDraft.eventType === 'host'"
                    [attr.aria-selected]="eventDraft.eventType === 'host'"
                    (click)="setEventDraftType('host')">Host</button>
          </div>

          <div class="event-draft-grid">
            <div>
              <label class="ihint">EVENT NAME *</label>
              <input type="text"
                     [(ngModel)]="eventDraft.eventName"
                     [class.input-error]="eventDraft.errors.eventName || eventDraft.errors.duplicate"
                     placeholder="e.g. Round 1 - GMHBA Stadium" />
            </div>
            <div *ngIf="eventDraft.eventType === 'archtics'">
              <label class="ihint">DATABASE *</label>
              <input type="text"
                     [(ngModel)]="eventDraft.database"
                     [class.input-error]="eventDraft.errors.database"
                     placeholder="e.g. AFL, BULLDOGS" />
            </div>
            <div *ngIf="eventDraft.eventType === 'host'">
              <label class="ihint">DATABASE</label>
              <div class="event-draft-host-db">HOST</div>
            </div>
            <div class="event-draft-desc">
              <label class="ihint">DESCRIPTION</label>
              <input type="text"
                     [(ngModel)]="eventDraft.description"
                     placeholder="(optional)" />
            </div>
          </div>
          <div class="draft-feedback">
            <div class="err-card" *ngIf="eventDraft.errors.eventName">
              <strong>✕ Event name required</strong>
            </div>
            <div class="err-card" *ngIf="eventDraft.errors.database">
              <strong>✕ Database required</strong>
            </div>
            <div class="err-card" *ngIf="eventDraft.errors.duplicate">
              <strong>✕ An event with this name already exists</strong>
            </div>
            <div class="warn-card" *ngIf="!eventDraft.errors.eventName && !eventDraft.errors.database && !eventDraft.errors.duplicate">
              <strong>ℹ Empty event</strong>
              <div>Save the event header now, then add extended-test rows under it in the next stage.</div>
            </div>
            <div class="draft-buttons">
              <button type="button" class="btn btn-cancel" (click)="cancelEventDraft()" [disabled]="saving">Cancel</button>
              <button type="button" class="btn btn-save" (click)="saveEventDraft()" [disabled]="saving">Save event</button>
            </div>
          </div>
        </div>

        <div class="db-filter" *ngIf="allDatabases.length > 1">
          <span class="db-filter-label">Databases</span>
          <button type="button"
                  class="db-chip"
                  *ngFor="let db of allDatabases; trackBy: trackString"
                  [class.db-chip--on]="isDatabaseSelected(db)"
                  (click)="toggleDatabase(db)">
            <span class="db-chip-tick">{{ isDatabaseSelected(db) ? '✓' : '' }}</span>
            {{ db }}
          </button>
          <button type="button"
                  class="db-chip-all"
                  *ngIf="!allDatabasesSelected()"
                  (click)="selectAllDatabases()">
            Select all
          </button>
          <button type="button"
                  class="db-chip-all"
                  *ngIf="selectedDatabases.size > 0"
                  (click)="clearDatabases()">
            Clear
          </button>
        </div>

        <div class="event-list">
          <div class="event" *ngFor="let ev of visibleEventsForListing(); trackBy: trackEvent">
            <div class="event-header" (click)="toggleEvent(ev)">
              <span class="event-caret" [class.event-caret--open]="ev.expanded" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4l5 4-5 4"/></svg>
              </span>
              <button type="button"
                      class="status-chip"
                      [class.status-chip--keep]="ev.keep"
                      [class.status-chip--remove]="!ev.keep"
                      (click)="$event.stopPropagation(); toggleKeep(ev)">
                {{ ev.keep ? 'KEEP' : 'REMOVE' }}
              </button>
              <span class="event-id">{{ ev.eventName }}</span>
              <span class="event-name">{{ ev.description }}</span>
              <span class="db-chip" *ngIf="ev.database">{{ ev.database }}</span>
              <span class="event-type-chip event-type-chip--host" *ngIf="ev.eventType === 'host'">HOST</span>
              <span class="event-meta" *ngIf="ev.eventType === 'host'">
                {{ visibleHostRows(ev).length }} host barcode{{ visibleHostRows(ev).length === 1 ? '' : 's' }}
              </span>
              <span class="event-meta" *ngIf="ev.eventType !== 'host'">
                {{ visibleApiRows(ev).length }} bc<span *ngIf="visibleExtendedRows(ev).length"> · {{ visibleExtendedRows(ev).length }} ext</span>
              </span>
            </div>

            <div class="event-body" *ngIf="ev.expanded">
              <div class="row-cols row-cols--header">
                <div class="col-db">DB</div>
                <div class="col-plan">PLAN</div>
                <div class="col-desc">DESCRIPTION</div>
                <div class="col-barcode">BARCODE</div>
                <div class="col-pc">PRICE CODE</div>
                <div class="col-srs">SECTION / ROW / SEAT</div>
              </div>

              <div class="row-cols api-row"
                   *ngFor="let bc of visibleApiRows(ev); trackBy: trackRow"
                   [class.row-cols--not-scanning]="notScanning(bc)">
                <div class="col-db mono-strong">{{ bc.database }}</div>
                <div class="col-plan mono">{{ bc.plan || '—' }}</div>
                <div class="col-desc">{{ bc.description }}</div>
                <div class="col-barcode mono">
                  <span *ngIf="notScanning(bc)" class="not-scanning-tag">(not scanning)</span>
                  <ng-container *ngIf="!notScanning(bc)">{{ bc.barcode || '—' }}</ng-container>
                </div>
                <div class="col-pc mono">{{ bc.priceCode || '—' }}</div>
                <div class="col-srs">{{ (bc.section || bc.row || bc.seat) ? (bc.section + ' · ' + bc.row + ' · ' + bc.seat) : '—' }}</div>
              </div>

              <div class="row-cols extended-row"
                   *ngFor="let ext of visibleExtendedRows(ev); trackBy: trackRow"
                   [class.row-cols--not-scanning]="notScanning(ext)">
                <div class="col-db mono-strong">{{ ext.database }}</div>
                <div class="col-plan mono">{{ ext.plan }}</div>
                <div class="col-desc">{{ ext.description }}</div>
                <div class="col-barcode mono">
                  <span *ngIf="notScanning(ext)" class="not-scanning-tag">(not scanning)</span>
                  <ng-container *ngIf="!notScanning(ext)">{{ ext.barcode || '—' }}</ng-container>
                </div>
                <div class="col-pc mono">{{ ext.priceCode || '—' }}</div>
                <div class="col-srs">{{ (ext.section || ext.row || ext.seat) ? (ext.section + ' · ' + ext.row + ' · ' + ext.seat) : '—' }}</div>
              </div>
            </div>
          </div>
        </div>

        <p class="hint" *ngIf="!visibleEventsForListing().length && hasDbFilterActive()">
          {{ selectedDatabases.size === 0
            ? 'No databases selected — every event is filtered out.'
            : 'No events match the selected database filter.' }}
          <a class="hint-link" (click)="selectAllDatabases()">Select all</a>
        </p>
        <p class="hint">Tip: this stage is for KEEP/REMOVE decisions only. Barcode editing and extended tests are configured in the next stage.</p>
      </div>
    </div>

    <ng-container *ngIf="currentStage.code === 'TST'">
      <div class="tst-hero">
        <div class="tst-hero-card">
          <div class="tst-hero-pill" [class.tst-hero-pill--idle]="!testRunning">
            <span class="tst-hero-pill-dot"></span>
            <span>{{ testRunning ? 'LIVE · TESTING' : 'TESTING' }}</span>
          </div>
          <div class="tst-hero-eyebrow">SCAN TEST PROGRESS</div>
          <div class="tst-hero-percent">
            {{ currentRun ? runProgressPercent : scanProgressPercent() }}<span class="tst-hero-percent-pct">%</span>
          </div>
          <div class="tst-hero-sub">
            <ng-container *ngIf="currentRun && runTotalTests > 0">
              {{ runCompletedTests }} of {{ runTotalTests }} scans complete<ng-container *ngIf="testRunning"> · {{ runStatusLabel }}</ng-container>
            </ng-container>
            <ng-container *ngIf="testRunning && (!currentRun || runTotalTests === 0)">
              Waiting for the run to start…
            </ng-container>
            <ng-container *ngIf="!currentRun && !testRunning">
              {{ scanCounts().pass + scanCounts().fail }} of {{ scanCounts().total }} barcodes tested
            </ng-container>
          </div>
          <div class="tst-hero-track">
            <div class="tst-hero-fill"
                 [style.width.%]="currentRun ? runProgressPercent : scanProgressPercent()"></div>
          </div>
        </div>

        <div class="tst-stat tst-stat--events">
          <div class="tst-stat-label">EVENTS</div>
          <div class="tst-stat-value">{{ totalEventsKept() }}</div>
        </div>
        <div class="tst-stat tst-stat--barcodes">
          <div class="tst-stat-label">BARCODES</div>
          <div class="tst-stat-value">{{ totalRowsAll() }}</div>
          <div class="tst-stat-meta">{{ totalApi() }} API · {{ totalExtended() }} ext</div>
        </div>
        <div class="tst-stat tst-stat--gates">
          <div class="tst-stat-label">GATES</div>
          <div class="tst-stat-value">{{ gateNames.length }}</div>
          <div class="tst-stat-meta">· {{ gatesWithRejects() }} reject expected</div>
        </div>
        <div class="tst-stat tst-stat--lastrun">
          <div class="tst-stat-label">LAST RUN</div>
          <ng-container *ngIf="lastRunFormatted() as run; else noLastRun">
            <div class="tst-stat-value tst-stat-value--small">{{ run.date }}</div>
            <div class="tst-stat-meta tst-stat-meta--strong">{{ run.time }}</div>
          </ng-container>
          <ng-template #noLastRun>
            <div class="tst-stat-value tst-stat-value--small">—</div>
            <div class="tst-stat-meta">No runs yet</div>
          </ng-template>
        </div>

        <div class="tst-result tst-result--pass">
          <div class="tst-result-label">PASSED</div>
          <div class="tst-result-value">{{ countsTotal().pass }}</div>
        </div>
        <div class="tst-result tst-result--fail">
          <div class="tst-result-label">FAILED</div>
          <div class="tst-result-value">{{ countsTotal().fail }}</div>
          <span class="tst-result-dot" *ngIf="countsTotal().fail > 0"></span>
        </div>
        <div class="tst-result tst-result--pending">
          <div class="tst-result-label">PENDING</div>
          <div class="tst-result-value">{{ countsTotal().pending }}</div>
        </div>
      </div>

      <tm1-banner *ngIf="testRunError" appearance="error" class="test-run-error">{{ testRunError }}</tm1-banner>
    </ng-container>

    <div class="surface-card events-card events-card--full events-card--sr"
         *ngIf="currentStage.code === 'SR'">
      <div class="card-pad">
        <div class="section-head">
          <div class="section-head-titleblock">
            <div class="section-eyebrow section-eyebrow--sr">SCAN RULES</div>
            <h2 class="section-title">Gate scan rules</h2>
          </div>
          <div class="section-head-actions">
            <button type="button"
                    class="btn-gate-groups"
                    appTooltip="Manage gate groups"
                    (click)="openGateGroupsModal()">
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="2" y="3" width="5" height="4" rx="1"/>
                <rect x="9" y="3" width="5" height="4" rx="1"/>
                <rect x="2" y="9" width="5" height="4" rx="1"/>
                <rect x="9" y="9" width="5" height="4" rx="1"/>
              </svg>
              <span>Grouped Gates</span>
              <span *ngIf="gateGroups.length" class="btn-gate-groups-count">{{ gateGroups.length }}</span>
            </button>
          </div>
        </div>

        <p class="section-sub">
          Drag a rule type onto each gate to define who may scan there. Events and Access Groups set the
          per-barcode expected results used in Set Test Config; Section &amp; Row, Ticket Usage and Price
          Code each seed a blank-barcode test row for the tester to complete. A gate with no rules rejects
          everything.
        </p>

        <details class="ticket-meta-panel" *ngIf="ruleEventsList.length">
          <summary class="ticket-meta-summary">
            <span class="ticket-meta-summary-title">Ticket types &amp; messages</span>
            <span class="ticket-meta-summary-hint">
              Scanner message (max {{ scannerMessageMaxLength }} chars) and one-time / multi-day usage per event
            </span>
          </summary>
          <div class="ticket-meta-list">
            <div class="ticket-meta-row" *ngFor="let ev of ruleEventsList; trackBy: trackRuleEvent">
              <div class="ticket-meta-event">
                <span class="ticket-meta-event-name">{{ ev.eventName }}</span>
                <span class="ticket-meta-event-desc" *ngIf="ev.description">{{ ev.description }}</span>
              </div>
              <label class="event-meta-field">
                <span class="event-meta-label">Ticket type &amp; message</span>
                <input #msgInput
                       type="text"
                       class="event-meta-input"
                       [attr.maxlength]="scannerMessageMaxLength"
                       [value]="eventMeta(ev.eventName).message || ''"
                       [disabled]="metaSaving"
                       placeholder="Shown on scanner"
                       (change)="setEventMessage(ev.eventName, msgInput.value)" />
                <span class="event-meta-count"
                      [class.event-meta-count--max]="msgInput.value.length >= scannerMessageMaxLength">
                  {{ msgInput.value.length }}/{{ scannerMessageMaxLength }}
                </span>
              </label>
              <label class="event-meta-field">
                <span class="event-meta-label">Ticket usage</span>
                <select class="event-meta-input"
                        [value]="eventMeta(ev.eventName).usage || ''"
                        [disabled]="metaSaving"
                        (change)="setEventUsage(ev.eventName, $any($event.target).value)">
                  <option value="">Not set</option>
                  <option value="one_time">One-time (single day)</option>
                  <option value="multi_day">Multi-day</option>
                  <option value="either">One-time or Multi-day</option>
                </select>
              </label>
            </div>
          </div>
        </details>

        <app-set-rules
          [gateNames]="gateNames"
          [events]="ruleEventsList"
          [existingRules]="scanRules"
          [gateGroups]="gateGroups"
          [accessGroups]="accessGroups"
          [priceCodes]="availablePriceCodes"
          [saving]="rulesSaving"
          [error]="rulesError"
          (save)="saveScanRules($event)"
          (cancel)="reloadScanRules()"
          (saveAccessGroups)="saveAccessGroups($event)">
        </app-set-rules>
      </div>
    </div>

    <div class="surface-card events-card events-card--full"
         [class.events-card--tc]="currentStage.code === 'TC'"
         [class.events-card--tm1]="currentStage.code === 'TM1'"
         [class.events-card--tst]="currentStage.code === 'TST'"
         *ngIf="showsMatrix">
      <div class="card-pad">
        <div class="section-head">
          <div class="section-head-titleblock">
            <div class="section-eyebrow section-eyebrow--tc" *ngIf="currentStage.code === 'TC'">TEST CONFIGURATION</div>
            <div class="section-eyebrow section-eyebrow--tm1" *ngIf="currentStage.code === 'TM1'">TM1 ENTRY CONFIGURATION</div>
            <div class="section-eyebrow" *ngIf="currentStage.code === 'TST'">SCAN TEST RESULTS</div>
            <h2 class="section-title" *ngIf="currentStage.code === 'TC'">Per-gate expected results</h2>
            <h2 class="section-title" *ngIf="currentStage.code === 'TM1'">Confirmed event listing</h2>
            <h2 class="section-title" *ngIf="currentStage.code === 'TST'">Confirmed event listing</h2>
          </div>

          <div class="section-head-actions">
            <div class="gate-view-toggle"
                 *ngIf="gateGroups.length"
                 role="tablist"
                 aria-label="Gate column view">
              <button type="button"
                      role="tab"
                      class="gate-view-btn"
                      [class.gate-view-btn--active]="gateGroupsView === 'grouped'"
                      [attr.aria-selected]="gateGroupsView === 'grouped'"
                      (click)="setGateGroupsView('grouped')">Grouped</button>
              <button type="button"
                      role="tab"
                      class="gate-view-btn"
                      [class.gate-view-btn--active]="gateGroupsView === 'individual'"
                      [attr.aria-selected]="gateGroupsView === 'individual'"
                      (click)="setGateGroupsView('individual')">Individual</button>
            </div>
            <button *ngIf="!matrixIsReadOnly"
                    type="button"
                    class="btn-gate-groups"
                    appTooltip="Define gate groups"
                    (click)="openGateGroupsModal()">
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="2" y="3" width="5" height="4" rx="1"/>
                <rect x="9" y="3" width="5" height="4" rx="1"/>
                <rect x="2" y="9" width="5" height="4" rx="1"/>
                <rect x="9" y="9" width="5" height="4" rx="1"/>
              </svg>
              <span>Grouped Gates</span>
              <span *ngIf="gateGroups.length" class="btn-gate-groups-count">{{ gateGroups.length }}</span>
            </button>
            <button *ngIf="currentStage.code === 'TST'"
                    type="button"
                    class="btn-tst-run"
                    [disabled]="!canRunTests()"
                    (click)="runAllTests()">
              <svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" aria-hidden="true">
                <path d="M4.5 3.2v9.6c0 .42.46.66.81.43l7.55-4.8a.5.5 0 0 0 0-.86L5.31 2.77a.5.5 0 0 0-.81.43z"/>
              </svg>
              <span>{{ testRunning && testRunningRow === null ? 'Running…' : 'Run all barcodes' }}</span>
            </button>
            <button *ngIf="currentStage.code === 'TST' && hasFailedRows()"
                    type="button"
                    class="btn-tst-failed"
                    [class.btn-tst-failed--on]="matrixOnlyFailed"
                    (click)="matrixOnlyFailed = !matrixOnlyFailed">
              {{ matrixOnlyFailed ? 'Showing failed (' + countsTotal().fail + ')' : 'Show only failed (' + countsTotal().fail + ')' }}
            </button>
            <button type="button"
                    class="btn-download"
                    [disabled]="!visibleEvents().length"
                    appTooltip="Download the visible matrix as an Excel file"
                    aria-label="Download matrix"
                    (click)="downloadMatrix()">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 3v10"/>
                <path d="M5 10l5 5 5-5"/>
                <path d="M3 17h14"/>
              </svg>
            </button>
            <button *ngIf="currentStage.code === 'TM1' || currentStage.code === 'TST'"
                    type="button"
                    class="btn-download btn-download--labelled"
                    [disabled]="!visibleEvents().length"
                    appTooltip="Export the confirmed event listing in the venue Access Control template format"
                    aria-label="Export access control config"
                    (click)="exportAccessControl()">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 3v10"/>
                <path d="M5 10l5 5 5-5"/>
                <path d="M3 17h14"/>
              </svg>
              <span>Export config</span>
            </button>
          </div>
        </div>

        <p class="section-sub" *ngIf="currentStage.code === 'TC'">
          Set the outcome you expect for every barcode at every gate. All rows default to accept; click a cell to toggle.
          You can also add extended tests per event, or click a barcode in the table to edit it.
        </p>
        <p class="section-sub" *ngIf="currentStage.code === 'TM1'">
          Client has confirmed the event listing. Configure TM1 entry for every event below before starting tests.
          Green = accept, red = reject.
        </p>
        <p class="section-sub" *ngIf="currentStage.code === 'TST'">
          Each cell keeps the shape of the expectation; fill colour shows the actual result.
        </p>


        <div class="lock-banner lock-banner--actions"
             *ngIf="currentStage.code === 'TM1' && clientConfirmation && canEditTm1"
             [class.lock-banner--unlocked]="tm1EditUnlocked">
          <button *ngIf="!tm1EditUnlocked"
                  type="button"
                  class="btn btn-edit-tm1"
                  (click)="openTm1EditModal()">
            Edit
          </button>
          <button *ngIf="tm1EditUnlocked"
                  type="button"
                  class="btn btn-save-tm1"
                  (click)="saveTm1Changes()">
            Save changes
          </button>
        </div>

        <div class="matrix-legend" *ngIf="currentStage.code !== 'TST'">
          <span class="legend-item"><span class="cell-shape cell-shape--accept"></span>Accept (default)</span>
          <span class="legend-item"><span class="cell-shape cell-shape--reject"></span>Reject — click to toggle</span>
          <span class="legend-item"><span class="legend-dot legend-dot--api"></span>Archtics-returned row</span>
          <span class="legend-item"><span class="legend-dot legend-dot--ext"></span>Extended test (client-added)</span>
          <span class="legend-item" *ngIf="totalHost()"><span class="legend-dot legend-dot--host"></span>Host barcode</span>
        </div>

        <div class="matrix-legend" *ngIf="currentStage.code === 'TST'">
          <span class="legend-item"><span class="cell-shape cell-shape--circle-pass"></span>Expected accept · passed</span>
          <span class="legend-item"><span class="cell-shape cell-shape--circle-fail"></span>Expected accept · FAILED</span>
          <span class="legend-item"><span class="cell-shape cell-shape--tri-pass"></span>Expected reject · passed</span>
          <span class="legend-item"><span class="cell-shape cell-shape--tri-fail"></span>Expected reject · FAILED</span>
          <span class="legend-item"><span class="cell-shape cell-shape--circle-pend"></span>Pending accept</span>
          <span class="legend-item"><span class="cell-shape cell-shape--tri-pend"></span>Pending reject</span>
        </div>

        <div class="db-filter" *ngIf="allDatabases.length > 1">
          <span class="db-filter-label">Databases</span>
          <button type="button"
                  class="db-chip"
                  *ngFor="let db of allDatabases; trackBy: trackString"
                  [class.db-chip--on]="isDatabaseSelected(db)"
                  (click)="toggleDatabase(db)">
            <span class="db-chip-tick">{{ isDatabaseSelected(db) ? '✓' : '' }}</span>
            {{ db }}
          </button>
          <button type="button"
                  class="db-chip-all"
                  *ngIf="!allDatabasesSelected()"
                  (click)="selectAllDatabases()">
            Select all
          </button>
          <button type="button"
                  class="db-chip-all"
                  *ngIf="selectedDatabases.size > 0"
                  (click)="clearDatabases()">
            Clear
          </button>
        </div>

        <div class="matrix-meta-row" *ngIf="currentStage.code !== 'TST'">
          <div class="matrix-meta">
            {{ totalRowsAll() }} barcodes across {{ keptEventsCount() }} events · {{ gateNames.length }} gates
            ({{ totalApi() }} Archtics<ng-container *ngIf="totalExtended()"> · {{ totalExtended() }} extended</ng-container><ng-container *ngIf="totalHost()"> · {{ totalHost() }} host</ng-container>).
          </div>
          <label class="filter-toggle">
            <input type="checkbox" [(ngModel)]="matrixOnlyExtended" />
            <span>Show only extended tests ({{ totalExtended() }})</span>
          </label>
        </div>

        <div class="bulk-bar" *ngIf="currentStage.code === 'TST' && selectedRowItems.size > 0">
          <div class="bulk-bar-summary">
            <strong>{{ selectedRowItems.size }}</strong> selected<span *ngIf="runnableSelectedCount !== selectedRowItems.size"> · {{ runnableSelectedCount }} runnable</span>
          </div>
          <div class="bulk-bar-actions">
            <button type="button"
                    class="btn-tst-run"
                    [disabled]="!canRunTests() || runnableSelectedCount === 0"
                    (click)="runSelectedTests()">
              <svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" aria-hidden="true">
                <path d="M4.5 3.2v9.6c0 .42.46.66.81.43l7.55-4.8a.5.5 0 0 0 0-.86L5.31 2.77a.5.5 0 0 0-.81.43z"/>
              </svg>
              <span>{{ runSelectedLabel }}</span>
            </button>
          </div>
          <button type="button"
                  class="bulk-bar-clear"
                  [disabled]="testRunning"
                  appTooltip="Clear selection"
                  (click)="clearSelection()">✕</button>
        </div>

        <div class="bulk-bar" *ngIf="!matrixIsReadOnly && selectedRowItems.size > 0">
          <div class="bulk-bar-summary">
            <strong>{{ selectedRowItems.size }}</strong> selected
          </div>
          <div class="bulk-bar-actions">
            <span class="bulk-bar-label">Set all gates:</span>
            <button type="button"
                    class="bulk-bar-btn bulk-bar-btn--accept"
                    [disabled]="bulkSaving || saving"
                    appTooltip="Set every gate to accept for selected rows"
                    (click)="bulkSetAllGates('Y')">
              <span class="cell-shape cell-shape--accept"></span>
              <span>Accept</span>
            </button>
            <button type="button"
                    class="bulk-bar-btn bulk-bar-btn--reject"
                    [disabled]="bulkSaving || saving"
                    appTooltip="Set every gate to reject for selected rows"
                    (click)="bulkSetAllGates('N')">
              <span class="cell-shape cell-shape--reject"></span>
              <span>Reject</span>
            </button>
            <span class="bulk-bar-divider" aria-hidden="true"></span>
            <span class="bulk-bar-label">Flip:</span>
            <button type="button"
                    class="bulk-bar-gate"
                    *ngFor="let col of matrixHeaderColumns(); trackBy: columnTrack"
                    [class.bulk-bar-gate--group]="col.kind === 'group'"
                    [disabled]="bulkSaving || saving"
                    [appTooltip]="(col.kind === 'group' ? col.label + ' group — ' : col.label + ' — ') + 'toggle accept/reject across selected rows'"
                    (click)="bulkSetColumn(col)">
              <span class="bulk-bar-gate-name">{{ col.label }}</span>
              <span *ngIf="bulkColumnState(col) === 'accept'" class="cell-shape cell-shape--accept"></span>
              <span *ngIf="bulkColumnState(col) === 'reject'" class="cell-shape cell-shape--reject"></span>
              <span *ngIf="bulkColumnState(col) === 'mixed'" class="bulk-gate-mixed">–</span>
            </button>
          </div>
          <button type="button"
                  class="bulk-bar-clear"
                  [disabled]="bulkSaving"
                  appTooltip="Clear selection"
                  (click)="clearSelection()">✕</button>
        </div>

        <div class="matrix-wrap" *ngIf="gateNames.length">
          <div class="matrix-grid"
               [class.matrix-grid--selectable]="canSelectRows"
               [style.--gate-col-width.px]="gateColWidth">
            <div class="matrix-meta-col">
              <div class="matrix-header-meta">
                <span class="mh-label mh-select" *ngIf="canSelectRows">
                  <input type="checkbox"
                         class="row-select"
                         [checked]="selectAllState === 'all'"
                         [indeterminate]="selectAllState === 'some'"
                         [disabled]="!selectableRowsAll().length"
                         appTooltip="Select all visible rows"
                         (change)="toggleSelectAll()" />
                </span>
                <span class="mh-label">DB</span>
                <span class="mh-label">PLAN</span>
                <span class="mh-label">DESCRIPTION</span>
                <span class="mh-label">BARCODE</span>
                <span class="mh-label">PRICE</span>
                <span class="mh-label">SEC / ROW / SEAT</span>
                <span class="mh-label">USAGE</span>
                <span class="mh-label">TYPE</span>
              </div>

              <ng-container *ngFor="let ev of visibleEvents(); trackBy: trackEvent">
                <div class="matrix-event-header"
                     [class.matrix-event-header--removed]="!ev.keep"
                     (click)="toggleMatrixExpanded(ev)">
                  <span class="event-select"
                        *ngIf="canSelectRows && ev.keep"
                        (click)="$event.stopPropagation()">
                    <input type="checkbox"
                           class="row-select"
                           [checked]="eventSelectState(ev) === 'all'"
                           [indeterminate]="eventSelectState(ev) === 'some'"
                           [disabled]="!selectableRowsForEvent(ev).length"
                           appTooltip="Select all rows in this event"
                           (change)="toggleEventSelected(ev)" />
                  </span>
                  <span class="event-caret" [class.event-caret--open]="ev.matrixExpanded" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4l5 4-5 4"/></svg>
                  </span>
                  <span class="event-id">{{ ev.eventName }}</span>
                  <span class="event-name">{{ ev.description }}</span>
                  <span class="db-chip" *ngIf="ev.database">{{ ev.database }}</span>
                  <span class="status-chip status-chip--remove" *ngIf="!ev.keep">REMOVED</span>
                  <span class="event-type-chip event-type-chip--host" *ngIf="ev.eventType === 'host'">HOST</span>
                  <span class="group-meta" *ngIf="ev.eventType === 'host'">
                    {{ visibleHostRows(ev).length }} host barcode{{ visibleHostRows(ev).length === 1 ? '' : 's' }}
                  </span>
                  <span class="group-meta" *ngIf="ev.eventType !== 'host'">
                    {{ visibleApiRows(ev).length }} Archtics · {{ visibleExtendedRows(ev).length }} extended
                  </span>
                  <button *ngIf="!matrixIsReadOnly && ev.keep"
                          type="button"
                          class="btn-add"
                          [disabled]="hasOpenDraft() || saving"
                          (click)="$event.stopPropagation(); startDraft(ev)">
                    {{ ev.eventType === 'host' ? '+ Add host barcode' : '+ Add extended test' }}
                  </button>
                </div>
                <ng-container *ngIf="ev.matrixExpanded">
                  <div class="mr-meta"
                       *ngFor="let row of matrixVisibleRows(ev); trackBy: trackRow"
                       [class.mr-meta--ext]="row.extended"
                       [class.mr-meta--removed]="!ev.keep"
                       [class.mr-meta--not-scanning]="notScanning(row)"
                       [class.mr-meta--selected]="canSelectRows && isRowSelected(row)"
                       [class.mr-meta--clickable]="currentStage.code === 'TST' && !notScanning(row)"
                       [class.mr-meta--pass]="currentStage.code === 'TST' && !notScanning(row) && rowResult(row) === 'pass'"
                       [class.mr-meta--fail]="currentStage.code === 'TST' && !notScanning(row) && rowResult(row) === 'fail'"
                       (click)="currentStage.code === 'TST' && openRowDetailModal(ev, row)">
                    <span class="mr-cell mr-select" *ngIf="canSelectRows" (click)="$event.stopPropagation()">
                      <input type="checkbox"
                             class="row-select"
                             [checked]="isRowSelected(row)"
                             [disabled]="!isRowSelectable(row)"
                             appTooltip="Select row"
                             (change)="toggleRowSelected(row)" />
                    </span>
                    <span class="mr-cell mono-strong">{{ row.database }}</span>
                    <span class="mr-cell mono">{{ row.plan || '—' }}</span>
                    <span class="mr-cell">{{ row.description || '—' }}</span>
                    <span class="mr-cell mono mr-cell-barcode">
                      <ng-container *ngIf="notScanning(row); else barcodeEditable">
                        <span class="not-scanning-tag">(not scanning)</span>
                      </ng-container>
                      <ng-template #barcodeEditable>
                        <ng-container *ngIf="!matrixIsReadOnly; else barcodeReadOnly">
                          <button *ngIf="row.barcode && !row.editingBarcode"
                                  type="button"
                                  class="btn-edit-barcode"
                                  [disabled]="saving"
                                  appTooltip="Click to edit"
                                  (click)="startBarcodeEdit(row)">
                            {{ row.barcode }}
                          </button>
                          <button *ngIf="!row.barcode && !row.editingBarcode"
                                  type="button"
                                  class="btn-add-barcode"
                                  [class.btn-add-barcode--awaiting]="row.extended && row.raw.ruleSeedKey"
                                  [disabled]="saving"
                                  [appTooltip]="row.extended && row.raw.ruleSeedKey
                                    ? 'Seeded from a rule — add a barcode to make this row testable'
                                    : 'Add a barcode to this row'"
                                  (click)="startBarcodeEdit(row)">
                            {{ row.extended && row.raw.ruleSeedKey ? 'Awaiting barcode' : '+ Add barcode' }}
                          </button>
                          <span *ngIf="row.editingBarcode" class="barcode-edit">
                            <input type="text"
                                   [(ngModel)]="row.barcodeDraft"
                                   [class.input-error]="row.barcodeError"
                                   placeholder="barcode"
                                   (keyup.enter)="saveBarcode(ev, row)"
                                   (keyup.escape)="cancelBarcodeEdit(row)" />
                            <button type="button" class="btn-inline btn-inline-save" [disabled]="saving" (click)="saveBarcode(ev, row)">✓</button>
                            <button type="button" class="btn-inline btn-inline-cancel" [disabled]="saving" (click)="cancelBarcodeEdit(row)">✕</button>
                          </span>
                        </ng-container>
                        <ng-template #barcodeReadOnly>{{ row.barcode || '(no barcode)' }}</ng-template>
                      </ng-template>
                    </span>
                    <span class="mr-cell mono">{{ row.priceCode || '—' }}</span>
                    <span class="mr-cell">{{ (row.section || row.row || row.seat) ? (row.section + ' · ' + row.row + ' · ' + row.seat) : '—' }}</span>
                    <span class="mr-cell">{{ usageDisplay(row) || '—' }}</span>
                    <span class="mr-cell mr-type">
                      <span class="tiny">{{ rowTypeLabel(row) }}</span>
                      <button *ngIf="!matrixIsReadOnly && row.extended && row.barcode && !notScanning(row)"
                              type="button"
                              class="btn-remove"
                              [disabled]="saving"
                              appTooltip="Remove extended test"
                              (click)="removeExtended(ev, row)">
                        ✕
                      </button>
                      <button *ngIf="currentStage.code === 'TST' && row.barcode && !notScanning(row)"
                              type="button"
                              class="btn-row-run"
                              [class.btn-row-run--running]="testRunningRow === row.item"
                              [disabled]="!canRunTests()"
                              [appTooltip]="'Run test for ' + row.barcode"
                              (click)="$event.stopPropagation(); runRowTest(row)">
                        <svg *ngIf="testRunningRow !== row.item" class="btn-row-run-icon" viewBox="0 0 16 16" width="10" height="10" fill="currentColor" aria-hidden="true">
                          <path d="M4.5 3.2v9.6c0 .42.46.66.81.43l7.55-4.8a.5.5 0 0 0 0-.86L5.31 2.77a.5.5 0 0 0-.81.43z"/>
                        </svg>
                        <svg *ngIf="testRunningRow === row.item" class="btn-row-run-spinner" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                          <path d="M8 2a6 6 0 1 1-6 6" opacity="0.3"/>
                          <path d="M8 2a6 6 0 0 1 6 6"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                </ng-container>
              </ng-container>
            </div>

            <div class="matrix-gate-col">
              <div class="matrix-header-gates">
                <span class="mh-gate"
                      *ngFor="let col of matrixHeaderColumns(); trackBy: columnTrack"
                      [class.mh-gate--group]="col.kind === 'group'"
                      [class.mh-gate--group-expanded]="col.kind === 'gate' && col.id.indexOf('expanded:') === 0"
                      [class.mh-gate--divergent]="isDivergentSplitColumn(col)"
                      [style.width.px]="slotPxWidth(col)"
                      [appTooltip]="col.kind === 'group' ? col.label + ' (group: ' + col.gates.join(', ') + ')' : col.label">
                  <button *ngIf="col.kind === 'group'"
                          type="button"
                          class="mh-group-toggle"
                          [class.mh-group-toggle--collapse]="isGroupExpanded(col.id)"
                          [appTooltip]="(isGroupExpanded(col.id) ? 'Collapse to single column' : 'Show every gate in this group separately') + ' for ' + col.label"
                          (click)="toggleGroupExpanded(col.id)">{{ isGroupExpanded(col.id) ? '−' : '+' }}</button>
                  <button *ngIf="col.kind === 'gate' && col.id.indexOf('expanded:') === 0"
                          type="button"
                          class="mh-group-toggle mh-group-toggle--collapse"
                          [appTooltip]="'Collapse this group back to a single column'"
                          (click)="collapseExpandedGate(col.id)">−</button>
                  <span class="mh-gate-label">{{ col.label }}</span>
                  <span class="mh-gate-group-icon"
                        *ngIf="col.kind === 'gate' && groupForGate(col.gates[0])"
                        [appTooltip]="'Part of group: ' + groupForGate(col.gates[0])!.name"
                        aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="2" width="7" height="7" rx="1.2"/>
                      <rect x="7" y="7" width="7" height="7" rx="1.2"/>
                    </svg>
                  </span>
                </span>
              </div>

              <ng-container *ngFor="let ev of visibleEvents(); trackBy: trackEvent">
                <div class="matrix-event-spacer matrix-event-spacer--gates"
                     [class.matrix-event-spacer--bulkable]="!matrixIsReadOnly && ev.keep">
                  <ng-container *ngIf="!matrixIsReadOnly && ev.keep; else gateSpacerPlain">
                    <ng-container *ngFor="let col of matrixHeaderColumns(); trackBy: columnTrack">
                      <button *ngIf="col.kind === 'gate'"
                              type="button"
                              class="bulk-gate-btn"
                              [style.width.px]="slotPxWidth(col)"
                              [disabled]="saving || eventGateState(ev, col.gates[0]) === 'empty'"
                              [appTooltip]="col.label + ' — toggle accept/reject for all ' + ev.eventName + ' rows'"
                              (click)="bulkToggleEventGate(ev, col.gates[0])">
                        <span *ngIf="eventGateState(ev, col.gates[0]) === 'accept'" class="cell-shape cell-shape--accept"></span>
                        <span *ngIf="eventGateState(ev, col.gates[0]) === 'reject'" class="cell-shape cell-shape--reject"></span>
                        <span *ngIf="eventGateState(ev, col.gates[0]) === 'mixed'" class="bulk-gate-mixed">–</span>
                        <span *ngIf="eventGateState(ev, col.gates[0]) === 'empty'" class="bulk-gate-mixed">·</span>
                      </button>
                      <button *ngIf="col.kind === 'group'"
                              type="button"
                              class="bulk-gate-btn bulk-gate-btn--group"
                              [class.bulk-gate-btn--divergent]="groupColumnIsDivergent(col)"
                              [style.width.px]="slotPxWidth(col)"
                              [disabled]="saving"
                              [appTooltip]="col.label + ' group — toggle accept/reject for all ' + ev.eventName + ' rows'"
                              (click)="bulkToggleEventGroup(ev, col)">
                        <span class="bulk-group-label">{{ groupColumnLabel(col) }}</span>
                      </button>
                    </ng-container>
                  </ng-container>
                  <ng-template #gateSpacerPlain>
                    <span class="bulk-gate-pad"
                          *ngFor="let col of matrixHeaderColumns(); trackBy: columnTrack"
                          [style.width.px]="slotPxWidth(col)"></span>
                  </ng-template>
                </div>
                <ng-container *ngIf="ev.matrixExpanded">
                  <div class="mr-cells"
                       *ngFor="let row of matrixVisibleRows(ev); trackBy: trackRow"
                       [class.mr-cells--ext]="row.extended"
                       [class.mr-cells--removed]="!ev.keep"
                       [class.mr-cells--not-scanning]="notScanning(row)"
                       [class.mr-cells--clickable]="currentStage.code === 'TST' && !notScanning(row)"
                       [class.mr-cells--pass]="currentStage.code === 'TST' && !notScanning(row) && rowResult(row) === 'pass'"
                       [class.mr-cells--fail]="currentStage.code === 'TST' && !notScanning(row) && rowResult(row) === 'fail'"
                       (click)="currentStage.code === 'TST' && openRowDetailModal(ev, row)">
                    <span *ngIf="notScanning(row)" class="not-scanning-banner">(not scanning)</span>
                    <ng-container *ngIf="!notScanning(row)">
                      <button type="button"
                              class="cell-btn"
                              [class.cell-btn--readonly]="matrixIsReadOnly"
                              [class.cell-btn--group]="col.kind === 'group' || (col.kind === 'gate' && (col.id.indexOf('expanded:') === 0 || col.id.indexOf('divergent:') === 0))"
                              *ngFor="let col of rowCellsForRow(row); trackBy: columnTrack"
                              [style.width.px]="slotPxWidth(col)"
                              [disabled]="saving || matrixIsReadOnly"
                              [appTooltip]="columnTooltip(row, col)"
                              (click)="toggleColumn(row, col)">
                        <ng-container *ngIf="currentStage.code !== 'TST'">
                          <span *ngIf="columnState(row, col) === 'accept'" class="cell-shape cell-shape--accept"></span>
                          <span *ngIf="columnState(row, col) === 'reject'" class="cell-shape cell-shape--reject"></span>
                          <span *ngIf="columnState(row, col) === 'mixed'" class="bulk-gate-mixed">–</span>
                        </ng-container>
                        <ng-container *ngIf="currentStage.code === 'TST'">
                          <span *ngIf="columnState(row, col) === 'accept' && columnResult(row, col) === 'pass'" class="cell-shape cell-shape--circle-pass"></span>
                          <span *ngIf="columnState(row, col) === 'accept' && columnResult(row, col) === 'fail'" class="cell-shape cell-shape--circle-fail"></span>
                          <span *ngIf="columnState(row, col) === 'accept' && columnResult(row, col) === 'pending'" class="cell-shape cell-shape--circle-pend"></span>
                          <span *ngIf="columnState(row, col) === 'reject' && columnResult(row, col) === 'pass'" class="cell-shape cell-shape--tri-pass"></span>
                          <span *ngIf="columnState(row, col) === 'reject' && columnResult(row, col) === 'fail'" class="cell-shape cell-shape--tri-fail"></span>
                          <span *ngIf="columnState(row, col) === 'reject' && columnResult(row, col) === 'pending'" class="cell-shape cell-shape--tri-pend"></span>
                          <span *ngIf="columnState(row, col) === 'mixed'" class="bulk-gate-mixed">–</span>
                        </ng-container>
                      </button>
                    </ng-container>
                  </div>
                </ng-container>
              </ng-container>
            </div>
          </div>
        </div>

        <p class="hint" *ngIf="matrixOnlyExtended && !visibleEvents().length">No extended tests have been added for this performance.</p>
        <p class="hint" *ngIf="!matrixOnlyExtended && !visibleEvents().length && hasDbFilterActive()">
          {{ selectedDatabases.size === 0
            ? 'No databases selected — every row is filtered out.'
            : 'No rows match the selected database filter.' }}
          <a class="hint-link" (click)="selectAllDatabases()">Select all</a>
        </p>
        <p class="hint" *ngIf="!gateNames.length">No gates configured for this venue. Configure gates in the Tools tab before setting test config.</p>
        <p class="hint" *ngIf="gateNames.length && !matrixIsReadOnly">
          Tip: click any cell to toggle accept ↔ reject. Defaults remain accept everywhere unless you change them.
        </p>
        <p class="hint" *ngIf="gateNames.length && matrixIsReadOnly">
          Read-only — this listing is locked. TM staff can re-open the performance to edit expectations.
        </p>
      </div>
    </div>

  </div>
</div>

<div class="audit-modal-backdrop" *ngIf="showAuditModal" (click)="$event.target === $event.currentTarget && closeAuditModal()">
  <div class="audit-modal" role="dialog" aria-modal="true" aria-labelledby="auditModalTitle">
    <div class="audit-modal-header">
      <div>
        <h2 id="auditModalTitle" class="audit-modal-title">Audit trail</h2>
        <div class="audit-modal-sub">{{ audit.length }} {{ audit.length === 1 ? 'entry' : 'entries' }}</div>
      </div>
      <button type="button" class="audit-modal-close" aria-label="Close" (click)="closeAuditModal()">×</button>
    </div>
    <div class="audit-modal-body">
      <div class="audit-list">
        <div class="audit-empty" *ngIf="!audit.length">No audit entries yet.</div>
        <div class="audit-entry" *ngFor="let entry of audit; trackBy: trackAuditEntry">
          <span class="audit-dot"
                [class.audit-dot--active]="entry.action === 'create'"
                [class.audit-dot--rollback]="entry.variant === 'rollback'"></span>
          <div class="audit-body">
            <div class="audit-line">{{ entry.summary }}</div>
            <div class="audit-meta">{{ formatAuditTime(entry.time) }} · {{ entry.username }}</div>
            <div class="audit-detail" *ngFor="let d of entry.details; trackBy: trackString">{{ d }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="audit-modal-backdrop" *ngIf="showRowDetailModal && rowDetailRow && rowDetailEvent" (click)="$event.target === $event.currentTarget && closeRowDetailModal()">
  <div class="audit-modal row-detail-modal" role="dialog" aria-modal="true" aria-labelledby="rowDetailTitle">
    <div class="audit-modal-header">
      <div>
        <h2 id="rowDetailTitle" class="audit-modal-title">Per-gate test results</h2>
        <div class="audit-modal-sub">{{ rowDetailEvent.eventName }}<span *ngIf="rowDetailRow.barcode"> · {{ rowDetailRow.barcode }}</span></div>
      </div>
      <button type="button" class="audit-modal-close" aria-label="Close" (click)="closeRowDetailModal()">×</button>
    </div>
    <div class="audit-modal-body">
      <div class="row-detail-summary">
        <div class="row-detail-summary-item">
          <span class="row-detail-summary-label">DB</span>
          <span class="row-detail-summary-value mono-strong">{{ rowDetailRow.database || '—' }}</span>
        </div>
        <div class="row-detail-summary-item" *ngIf="rowDetailRow.section || rowDetailRow.row || rowDetailRow.seat">
          <span class="row-detail-summary-label">SEC / ROW / SEAT</span>
          <span class="row-detail-summary-value">{{ rowDetailRow.section }} · {{ rowDetailRow.row }} · {{ rowDetailRow.seat }}</span>
        </div>
        <div class="row-detail-summary-item" *ngIf="rowDetailRow.priceCode">
          <span class="row-detail-summary-label">PRICE CODE</span>
          <span class="row-detail-summary-value mono">{{ rowDetailRow.priceCode }}</span>
        </div>
        <div class="row-detail-summary-item" *ngIf="usageDisplay(rowDetailRow)">
          <span class="row-detail-summary-label">TICKET USAGE</span>
          <span class="row-detail-summary-value">{{ usageDisplay(rowDetailRow) }}</span>
        </div>
        <div class="row-detail-summary-item">
          <span class="row-detail-summary-label">OVERALL</span>
          <span class="row-detail-result"
                [class.row-detail-result--pass]="rowResult(rowDetailRow) === 'pass'"
                [class.row-detail-result--fail]="rowResult(rowDetailRow) === 'fail'"
                [class.row-detail-result--pending]="rowResult(rowDetailRow) === 'pending'">
            {{ rowResult(rowDetailRow) | uppercase }}
          </span>
        </div>
      </div>

      <table class="row-detail-table">
        <thead>
          <tr>
            <th>Gate</th>
            <th>Expected</th>
            <th>Result</th>
            <th>Last test</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let g of gateNames; trackBy: trackGate">
            <td class="row-detail-gate">{{ g }}</td>
            <td>
              <span class="row-detail-expected"
                    [class.row-detail-expected--accept]="cellExpected(rowDetailRow, g) === 'Y'"
                    [class.row-detail-expected--reject]="cellExpected(rowDetailRow, g) === 'N'">
                {{ cellExpected(rowDetailRow, g) === 'Y' ? 'Accept' : 'Reject' }}
              </span>
            </td>
            <td>
              <span class="row-detail-result"
                    [class.row-detail-result--pass]="cellResult(rowDetailRow, g) === 'pass'"
                    [class.row-detail-result--fail]="cellResult(rowDetailRow, g) === 'fail'"
                    [class.row-detail-result--pending]="cellResult(rowDetailRow, g) === 'pending'">
                {{ cellResult(rowDetailRow, g) | uppercase }}
              </span>
            </td>
            <td class="row-detail-time">{{ cellLastTime(rowDetailRow, g) ? formatAuditTime(cellLastTime(rowDetailRow, g)!) : '—' }}</td>
            <td class="row-detail-message">{{ cellResultMessage(rowDetailRow, g) || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>

<app-advance-stage *ngIf="showAdvanceModal && nextStage"
                   [fromStage]="currentStage"
                   [toStage]="nextStage"
                   [totalStages]="stages.length"
                   [performanceSubtitle]="performanceSubtitle"
                   [currentUserEmail]="currentUserEmail"
                   [saving]="advanceSaving"
                   [saveError]="advanceError"
                   direction="forward"
                   (confirmed)="confirmAdvance($event)"
                   (cancelled)="cancelAdvance()">
</app-advance-stage>

<div class="confirm-modal-backdrop"
     *ngIf="draftEvent as ev"
     (click)="$event.target === $event.currentTarget && cancelDraft(ev)">
  <div class="confirm-modal draft-modal" role="dialog" aria-modal="true" aria-labelledby="draftModalTitle">
    <div class="confirm-header">
      <div>
        <h2 id="draftModalTitle" class="confirm-title">
          Add {{ ev.eventType === 'host' ? 'host barcode' : 'extended test' }}
        </h2>
        <div class="confirm-sub">{{ ev.eventName }}<span *ngIf="ev.description"> · {{ ev.description }}</span></div>
      </div>
      <button type="button" class="confirm-close" aria-label="Close" (click)="cancelDraft(ev)" [disabled]="saving">×</button>
    </div>

    <div class="confirm-content" *ngIf="ev.draft">
      <div class="draft-modal-grid">
        <div class="draft-field">
          <label class="ihint">DB *</label>
          <input type="text" [(ngModel)]="ev.draft.database" [class.input-error]="ev.draftErrors.db" placeholder="e.g. AFL" />
        </div>
        <div class="draft-field">
          <label class="ihint">PLAN</label>
          <input type="text" [(ngModel)]="ev.draft.plan" />
        </div>
        <div class="draft-field draft-field--wide">
          <label class="ihint">DESCRIPTION</label>
          <input type="text" [(ngModel)]="ev.draft.description" />
        </div>
        <div class="draft-field draft-field--wide">
          <label class="ihint">BARCODE *</label>
          <input type="text" [(ngModel)]="ev.draft.barcode" [class.input-error]="ev.draftErrors.barcode" placeholder="Enter barcode" />
        </div>
        <div class="draft-field">
          <label class="ihint">PRICE CODE</label>
          <input type="text" [(ngModel)]="ev.draft.priceCode" [class.input-error]="ev.draftErrors.locator" placeholder="e.g. A3" />
        </div>
        <div class="draft-field draft-field--srs">
          <label class="ihint">SEC / ROW / SEAT</label>
          <div class="srs-inputs">
            <input type="text" [(ngModel)]="ev.draft.section" [class.input-error]="ev.draftErrors.locator" placeholder="sec" />
            <input type="text" [(ngModel)]="ev.draft.row" [class.input-error]="ev.draftErrors.locator" placeholder="row" />
            <input type="text" [(ngModel)]="ev.draft.seat" [class.input-error]="ev.draftErrors.locator" placeholder="seat" />
          </div>
        </div>
      </div>

      <div class="draft-feedback">
        <div class="err-card" *ngIf="ev.draftErrors.barcode">
          <strong>✕ Barcode required</strong>
          <div>Enter a barcode for this test row.</div>
        </div>
        <div class="err-card" *ngIf="ev.draftErrors.locator">
          <strong>✕ Missing locating field</strong>
          <div>At least one of PRICE CODE, SECTION, ROW, or SEAT must be filled.</div>
        </div>
        <div class="err-card" *ngIf="ev.draftErrors.db">
          <strong>✕ DB required</strong>
          <div>Enter a database name (e.g. AFL, BULLDOGS).</div>
        </div>
      </div>

      <div class="confirm-actions">
        <tm1-button variant="outline" [disabled]="saving" (click)="cancelDraft(ev)">Cancel</tm1-button>
        <tm1-button variant="solid" [disabled]="saving || !validateDraft(ev).ok" (click)="saveDraft(ev)">
          Save test row
        </tm1-button>
      </div>
    </div>
  </div>
</div>

<div class="confirm-modal-backdrop" *ngIf="showTm1EditModal" (click)="$event.target === $event.currentTarget && cancelTm1Edit()">
  <div class="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="tm1EditTitle">
    <div class="confirm-header">
      <div>
        <h2 id="tm1EditTitle" class="confirm-title">Unlock TM1 stage for edits?</h2>
        <div class="confirm-sub">Editing the locked stage</div>
      </div>
      <button type="button" class="confirm-close" aria-label="Close" (click)="cancelTm1Edit()">×</button>
    </div>

    <div class="confirm-content">
      <p class="confirm-body">
        You are about to unlock the locked TM1 entry configuration. While unlocked, every cell change will save immediately,
        same as the test-config stage. Click <strong>Save changes</strong> when you're done to lock everything again.
      </p>

      <div class="confirm-block">
        <label class="confirm-label" for="tm1EditConfirmation">
          Type <strong>{{ tm1EditPhrase }}</strong> to unlock editing
        </label>
        <input id="tm1EditConfirmation"
               type="text"
               class="confirm-input"
               [class.confirm-input--ok]="tm1EditPhraseOk()"
               [(ngModel)]="tm1EditConfirmation"
               autocomplete="off"
               [attr.placeholder]="'type ' + tm1EditPhrase" />
      </div>

      <div class="confirm-actions">
        <tm1-button variant="outline" (click)="cancelTm1Edit()">Cancel</tm1-button>
        <tm1-button variant="solid" [disabled]="!tm1EditPhraseOk()" (click)="confirmTm1Edit()">
          Unlock
        </tm1-button>
      </div>
    </div>
  </div>
</div>

<div class="confirm-modal-backdrop" *ngIf="importPreview" (click)="$event.target === $event.currentTarget && cancelImport()">
  <div class="confirm-modal import-modal" role="dialog" aria-modal="true" aria-labelledby="importTitle">
    <div class="confirm-header">
      <div>
        <h2 id="importTitle" class="confirm-title">Import event listing</h2>
        <div class="confirm-sub">{{ importPreview.fileName }}</div>
      </div>
      <button type="button" class="confirm-close" aria-label="Close" (click)="cancelImport()">×</button>
    </div>

    <div class="confirm-content">
      <p class="confirm-body">
        Review the KEEP / REMOVE changes below. Only events that match a current event are updated;
        nothing is saved until you confirm.
      </p>

      <div class="import-error" *ngIf="importError">{{ importError }}</div>

      <div class="import-conflicts" *ngIf="importPreview.conflicts.length">
        <div class="import-section-title import-section-title--err">
          Conflicting statuses — fix the file before importing
        </div>
        <p class="import-note">
          These events have both KEEP and REMOVE rows in the file, so the intended status is ambiguous.
        </p>
        <ul class="import-list">
          <li *ngFor="let name of importPreview.conflicts">{{ name }}</li>
        </ul>
      </div>

      <div class="import-summary" *ngIf="!importPreview.conflicts.length">
        <div class="import-section-title" *ngIf="importPreview.changes.length">
          {{ importPreview.changes.length }} event(s) will change
        </div>
        <ul class="import-list" *ngIf="importPreview.changes.length">
          <li *ngFor="let change of importPreview.changes">
            <span class="import-event">{{ change.eventName }}</span>
            <span class="import-flip">
              <span class="import-flag" [class.import-flag--keep]="change.from === 'KEEP'" [class.import-flag--remove]="change.from === 'REMOVE'">{{ change.from }}</span>
              →
              <span class="import-flag" [class.import-flag--keep]="change.to === 'KEEP'" [class.import-flag--remove]="change.to === 'REMOVE'">{{ change.to }}</span>
            </span>
          </li>
        </ul>
        <p class="import-note" *ngIf="!importPreview.changes.length">
          No KEEP / REMOVE changes were found — the file matches the current listing.
        </p>
        <p class="import-note" *ngIf="importPreview.unchangedCount">
          {{ importPreview.unchangedCount }} matched event(s) are already up to date.
        </p>
      </div>

      <div class="import-warn" *ngIf="importPreview.unmatchedFileEvents.length">
        <div class="import-section-title import-section-title--warn">
          {{ importPreview.unmatchedFileEvents.length }} event(s) in the file don't match a current event — ignored
        </div>
        <ul class="import-list import-list--muted">
          <li *ngFor="let name of importPreview.unmatchedFileEvents">{{ name }}</li>
        </ul>
      </div>

      <div class="import-warn" *ngIf="importPreview.missingFromFile.length">
        <div class="import-section-title import-section-title--warn">
          {{ importPreview.missingFromFile.length }} current event(s) are not in the file — left unchanged
        </div>
        <ul class="import-list import-list--muted">
          <li *ngFor="let name of importPreview.missingFromFile">{{ name }}</li>
        </ul>
      </div>

      <div class="confirm-actions">
        <tm1-button variant="outline" (click)="cancelImport()">Cancel</tm1-button>
        <tm1-button variant="solid" [disabled]="!canConfirmImport()" (click)="confirmImport()">
          {{ importSaving ? 'Applying…' : 'Apply changes' }}
        </tm1-button>
      </div>
    </div>
  </div>
</div>

<app-advance-stage *ngIf="showBackModal && previousStage"
                   [fromStage]="currentStage"
                   [toStage]="previousStage"
                   [totalStages]="stages.length"
                   [performanceSubtitle]="performanceSubtitle"
                   [currentUserEmail]="currentUserEmail"
                   [saving]="backSaving"
                   [saveError]="backError"
                   direction="back"
                   (confirmed)="confirmBack($event)"
                   (cancelled)="cancelBack()">
</app-advance-stage>

<app-gate-groups *ngIf="showGateGroupsModal"
                 [gateNames]="gateNames"
                 [existingGroups]="gateGroups"
                 [saving]="gateGroupsSaving"
                 [error]="gateGroupsError"
                 [copyTargets]="copyTargets"
                 [copyLoading]="copyLoading"
                 [copyRunning]="copyRunning"
                 [copyError]="copyError"
                 [copyDone]="copyDone"
                 (save)="saveGateGroups($event)"
                 (cancel)="closeGateGroupsModal()"
                 (openCopy)="onOpenCopy()"
                 (closeCopy)="onCloseCopy()"
                 (copy)="onCopyTargets($event)">
</app-gate-groups>
